import type { RouteObject } from 'react-router';

import { RequirePermission } from '@saniplus/ui';

import { PermissionFallback } from '../../components/shared/PermissionFallback';

/**
 * The `Horaires` section, as route objects the app's router mounts.
 *
 * The feature exports routes rather than editing `app/router.tsx` so that four
 * features can land in parallel without three of them conflicting on one file.
 * Every module is lazy: the calendar pulls in the whole scheduling plot and
 * `luxon`, and a supervisor who only reviews punches should never download it.
 *
 * Two gates, not one, because the section spans two permissions: the calendar
 * and the open-shift queue read shifts (`shift:read`), the series screens read
 * recurring series (`series:read`), and a supervisor can hold one without the
 * other. Hiding the nav link is not enough — a typed URL, a bookmark or a stale
 * tab reaches the page anyway. Neither is a security control (the API scopes
 * and answers 404 on its own); they exist so the UI never offers a door that is
 * already locked, and they render an explanation rather than redirecting.
 *
 * `/horaires/series/new` sits **before** `/horaires/series/:id` on purpose —
 * react-router ranks static segments above dynamic ones, but stating the order
 * keeps it obvious that `new` is not a series id. `/horaires/series/:id/modifier`
 * (the edit wizard) sits after both: it is strictly deeper than `:id` (the
 * detail page) so react-router's own specificity ranking already resolves it
 * correctly regardless of array order, but it is listed last to keep the
 * static-before-dynamic story readable top to bottom.
 */
export const routes: RouteObject[] = [
  {
    path: 'horaires',
    children: [
      {
        element: <RequirePermission permission="shift:read" fallback={<PermissionFallback />} />,
        children: [
          {
            path: 'calendrier',
            lazy: async () => ({
              Component: (await import('./calendrier/CalendrierPage')).CalendrierPage,
            }),
          },
          {
            path: 'ouverts',
            lazy: async () => ({
              Component: (await import('./ouverts/OpenShiftsPage')).OpenShiftsPage,
            }),
          },
          {
            path: 'ouverts/affectation',
            lazy: async () => ({
              Component: (await import('./ouverts/BulkAssignPage')).BulkAssignPage,
            }),
          },
        ],
      },
      {
        element: <RequirePermission permission="series:read" fallback={<PermissionFallback />} />,
        children: [
          {
            path: 'series',
            lazy: async () => ({
              Component: (await import('./series/SeriesListPage')).SeriesListPage,
            }),
          },
          {
            path: 'series/new',
            lazy: async () => ({
              Component: (await import('./series/SeriesWizardPage')).SeriesWizardPage,
            }),
          },
          {
            path: 'series/:id',
            lazy: async () => ({
              Component: (await import('./series/SeriesDetailPage')).SeriesDetailPage,
            }),
          },
          {
            path: 'series/:id/modifier',
            lazy: async () => ({
              Component: (await import('./series/SeriesWizardPage')).SeriesWizardPage,
            }),
          },
        ],
      },
    ],
  },
];
